import React, { Component } from 'react';
import {
  Card,
  Button,
  Table,
  Input,
  Modal,
  Form,
  Tag,
  Space,
  Popconfirm,
  message,
  Empty,
  Tooltip,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ImportOutlined,
  ExportOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
} from '@ant-design/icons';

const { TextArea } = Input;

class FAQPriorityTab extends Component {
  state = {
    searchText: '',
    editModalVisible: false,
    editingFAQ: null,
    localFAQs: [],
  };

  constructor(props) {
    super(props);
    this.state.localFAQs = props.faqs || [];
  }

  componentDidUpdate(prevProps) {
    if (prevProps.faqs !== this.props.faqs) {
      this.setState({ localFAQs: this.props.faqs || [] });
    }
  }

  // Filter FAQs based on search
  getFilteredFAQs = () => {
    const { searchText, localFAQs } = this.state;
    if (!searchText) return localFAQs;
    return localFAQs.filter(
      (faq) =>
        faq.question?.toLowerCase().includes(searchText.toLowerCase()) ||
        faq.answer?.toLowerCase().includes(searchText.toLowerCase()) ||
        faq.tags?.some((tag) => tag.toLowerCase().includes(searchText.toLowerCase()))
    );
  };

  openEditModal = (faq = null) => {
    this.setState({
      editModalVisible: true,
      editingFAQ: faq
        ? { ...faq }
        : {
            id: `faq_${Date.now()}`,
            question: '',
            answer: '',
            priority: 0,
            tags: [],
            isActive: true,
          },
    });
  };

  closeEditModal = () => {
    this.setState({
      editModalVisible: false,
      editingFAQ: null,
    });
  };

  saveFAQ = () => {
    const { editingFAQ, localFAQs } = this.state;

    if (!editingFAQ.question || !editingFAQ.answer) {
      message.warning('Vui lòng nhập câu hỏi và câu trả lời');
      return;
    }

    let newFAQs;
    if (editingFAQ.id && localFAQs.some((f) => f.id === editingFAQ.id)) {
      // Update existing
      newFAQs = localFAQs.map((f) =>
        f.id === editingFAQ.id ? editingFAQ : f
      );
    } else {
      // Create new - Set priority automatically
      const newFaq = {
        ...editingFAQ,
        priority: localFAQs.length > 0 ? Math.max(...localFAQs.map(f => f.priority || 0)) + 1 : 1
      };
      newFAQs = [...localFAQs, newFaq];
    }

    this.setState({
      localFAQs: newFAQs,
      editModalVisible: false,
      editingFAQ: null,
    });

    if (this.props.onChange) {
      this.props.onChange(newFAQs);
    }

    // Tự động lưu xuống database ngay lập tức
    if (this.props.onSave) {
      setTimeout(() => this.props.onSave(), 100);
    }

    message.success(editingFAQ.id && localFAQs.some((f) => f.id === editingFAQ.id)
      ? 'FAQ đã được cập nhật'
      : 'FAQ đã được tạo');
  };

  deleteFAQ = (id) => {
    const newFAQs = this.state.localFAQs.filter((f) => f.id !== id);
    this.setState({ localFAQs: newFAQs });

    if (this.props.onChange) {
      this.props.onChange(newFAQs);
    }

    // Tự động lưu xuống database ngay lập tức
    if (this.props.onSave) {
      setTimeout(() => this.props.onSave(), 100);
    }

    message.success('FAQ đã được xóa');
  };

  toggleFAQActive = (faq) => {
    const newFAQs = this.state.localFAQs.map((f) =>
      f.id === faq.id ? { ...f, isActive: !f.isActive } : f
    );
    this.setState({ localFAQs: newFAQs });

    if (this.props.onChange) {
      this.props.onChange(newFAQs);
    }
  };

  movePriority = (index, direction) => {
    const faqs = [...this.state.localFAQs];
    const newIndex = index + direction;

    if (newIndex < 0 || newIndex >= faqs.length) return;

    // Swap
    [faqs[index], faqs[newIndex]] = [faqs[newIndex], faqs[index]];

    // Update priority
    faqs.forEach((faq, idx) => {
      faq.priority = faqs.length - idx;
    });

    this.setState({ localFAQs: faqs });

    if (this.props.onChange) {
      this.props.onChange(faqs);
    }

    // Tự động lưu xuống database ngay lập tức khi thay đổi thứ tự
    if (this.props.onSave) {
      setTimeout(() => this.props.onSave(), 100);
    }
  };

  columns = [
    {
      title: 'Độ ưu tiên',
      key: 'priority',
      width: 80,
      render: (_, record, index) => {
        const filtered = this.getFilteredFAQs();
        const isFirst = index === 0;
        const isLast = index === filtered.length - 1;
        return (
          <Space direction="vertical" size={0}>
            <Button
              type="text"
              size="small"
              disabled={isFirst}
              onClick={() => this.movePriority(index, -1)}
              icon={<ArrowUpOutlined style={{ fontSize: '12px' }} />}
            />
            <Button
              type="text"
              size="small"
              disabled={isLast}
              onClick={() => this.movePriority(index, 1)}
              icon={<ArrowDownOutlined style={{ fontSize: '12px' }} />}
            />
          </Space>
        );
      },
    },
    {
      title: 'Câu hỏi',
      dataIndex: 'question',
      key: 'question',
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{text}</div>
          {record.tags && record.tags.length > 0 && (
            <div style={{ marginTop: 4 }}>
              {record.tags.map((tag) => (
                <Tag key={tag} size="small">
                  {tag}
                </Tag>
              ))}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Câu trả lời',
      dataIndex: 'answer',
      key: 'answer',
      ellipsis: true,
    },
    {
      title: 'Trạng thái',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      render: (isActive, record) => (
        <Tag color={isActive ? 'green' : 'default'}>
          {isActive ? 'Hoạt động' : 'Tắt'}
        </Tag>
      ),
    },
    {
      title: 'Hành động',
      key: 'actions',
      width: 150,
      render: (_, record, index) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => this.openEditModal(record)}
          >
            Sửa
          </Button>
          <Popconfirm
            title="Xóa FAQ này?"
            onConfirm={() => this.deleteFAQ(record.id)}
          >
            <Button type="text" danger icon={<DeleteOutlined />}>
              Xóa
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  render() {
    const { onSave, loading } = this.props;
    const { searchText, editModalVisible, editingFAQ } = this.state;
    const filteredFAQs = this.getFilteredFAQs();

    return (
      <div className="faq-priority-tab">
        <Card
          title="Câu hỏi thường gặp (FAQ)"
          extra={
            <Space>

              <Button type="primary" onClick={onSave} loading={loading}>
                Lưu thay đổi
              </Button>
            </Space>
          }
        >
          {/* Search and Actions */}
          <div style={{ marginBottom: 16, display: 'flex', gap: 12 }}>
            <Input
              placeholder="Tìm kiếm FAQ..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => this.setState({ searchText: e.target.value })}
              style={{ width: 300 }}
              allowClear
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => this.openEditModal()}
            >
              Thêm FAQ
            </Button>
          </div>

          {/* FAQ Table */}
          <Table
            columns={this.columns}
            dataSource={filteredFAQs}
            rowKey="id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Tổng ${total} FAQ`,
            }}
            locale={{
              emptyText: (
                <Empty
                  description="Chưa có FAQ nào"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                >
                  <Button type="primary" onClick={() => this.openEditModal()}>
                    Thêm FAQ đầu tiên
                  </Button>
                </Empty>
              ),
            }}
          />

          {/* Edit Modal */}
          <Modal
            title={editingFAQ?.id && this.state.localFAQs.some((f) => f.id === editingFAQ.id)
              ? 'Chỉnh sửa FAQ'
              : 'Tạo FAQ mới'}
            open={editModalVisible}
            onOk={this.saveFAQ}
            onCancel={this.closeEditModal}
            width={600}
            okText="Lưu"
            cancelText="Hủy"
          >
            {editingFAQ && (
              <Form layout="vertical">
                <Form.Item label="Câu hỏi" required>
                  <TextArea
                    rows={2}
                    value={editingFAQ.question}
                    onChange={(e) =>
                      this.setState({
                        editingFAQ: { ...editingFAQ, question: e.target.value },
                      })
                    }
                    placeholder="Nhập câu hỏi..."
                  />
                </Form.Item>

                <Form.Item label="Câu trả lời" required>
                  <TextArea
                    rows={4}
                    value={editingFAQ.answer}
                    onChange={(e) =>
                      this.setState({
                        editingFAQ: { ...editingFAQ, answer: e.target.value },
                      })
                    }
                    placeholder="Nhập câu trả lời..."
                  />
                </Form.Item>

                <Form.Item label="Tags (phân loại)">
                  <Input
                    value={editingFAQ.tags?.join(', ') || ''}
                    onChange={(e) => {
                      const tags = e.target.value
                        .split(',')
                        .map((t) => t.trim())
                        .filter((t) => t);
                      this.setState({
                        editingFAQ: { ...editingFAQ, tags },
                      });
                    }}
                    placeholder="Nhập tags, cách nhau bằng dấu phẩy"
                  />
                </Form.Item>

                <Form.Item label="Trạng thái">
                  <Tag
                    color={editingFAQ.isActive ? 'green' : 'default'}
                    style={{ cursor: 'pointer' }}
                    onClick={() =>
                      this.setState({
                        editingFAQ: {
                          ...editingFAQ,
                          isActive: !editingFAQ.isActive,
                        },
                      })
                    }
                  >
                    {editingFAQ.isActive ? 'Hoạt động' : 'Tắt'}
                  </Tag>
                </Form.Item>
              </Form>
            )}
          </Modal>
        </Card>
      </div>
    );
  }
}

export default FAQPriorityTab;
